import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { t } from "../i18n/index.ts";
import { ignore } from "../lib/ignore.ts";
import {
  type AgentPicture,
  type Crop,
  type Drawable,
  dragged,
  encodeSquare,
  initialCrop,
  MAX_ZOOM,
  minZoom,
  PROVIDER_PICTURES,
  placement,
  presetName,
  ROBOT_PICTURES,
  squareRegion,
  zoomed,
} from "../lib/pictures.ts";
import { useAction } from "../lib/resource.ts";
import { toast } from "../lib/toast.ts";
import { useId } from "../lib/uid.ts";
import { Photo } from "./Avatar.tsx";
import { Button } from "./Button.tsx";
import { Dialog, DialogActions } from "./Dialog.tsx";
import { PresetPicture } from "./PresetPicture.tsx";
import { FOCUS } from "./styles.ts";

/** Preview side, in pixels. */
const BOX = 240;
/** How far an arrow key moves the picture, in preview pixels. */
const STEP = 12;
const ZOOM_STEP = 0.01;
const ARROWS: Record<string, { dx: number; dy: number }> = {
  ArrowLeft: { dx: STEP, dy: 0 },
  ArrowRight: { dx: -STEP, dy: 0 },
  ArrowUp: { dx: 0, dy: STEP },
  ArrowDown: { dx: 0, dy: -STEP },
};

/** Image types offered when picking a file (the browser decodes it; it's sent as WebP or PNG). */
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/avif,image/heic";

/** A file the browser decoded, with a URL to show it. */
interface Loaded {
  image: Drawable;
  url: string;
}

/** Decodes `file`; null when the browser can't. */
async function load(file: File): Promise<Loaded | null> {
  try {
    const image = await createImageBitmap(file);
    return { image, url: URL.createObjectURL(file) };
  } catch {
    return null;
  }
}

/**
 * Cropping a picked image to a square, in a dialog: drag it (or the arrow keys) to move it, the slider to zoom in, or
 * out until all of it fits (a wide logo, say).
 * `round`: shown in a circle (an avatar), else a rounded square (a logo). `onDone` gets the square, scaled down and
 * encoded, and saves it (the dialog closes when it's done).
 */
function CropDialog(p: {
  file: File | null;
  round: boolean;
  onClose: () => void;
  onDone: (picture: Blob) => Promise<void> | void;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [crop, setCrop] = useState<Crop>({ zoom: 1, cx: 0, cy: 0 });
  // Where the pointer was last, while dragging.
  const drag = useRef({ x: 0, y: 0 });
  const { file, onClose } = p;
  useEffect(() => {
    let live = true;
    let shown: string | null = null;
    setLoaded(null);
    if (file) {
      load(file).then((l) => {
        if (!live) {
          return;
        }
        if (!l) {
          toast(t("picture.unreadable"), "error");
          onClose();
          return;
        }
        shown = l.url;
        setCrop(initialCrop({ w: l.image.width, h: l.image.height }));
        setLoaded(l);
      }, ignore);
    }
    return () => {
      live = false;
      if (shown) {
        URL.revokeObjectURL(shown);
      }
    };
  }, [file, onClose]);
  const [save, saving] = useAction(async () => {
    if (!loaded) {
      return;
    }
    const size = { w: loaded.image.width, h: loaded.image.height };
    await p.onDone(await encodeSquare(loaded.image, squareRegion(size, crop)));
    onClose();
  });
  const size = { w: loaded?.image.width ?? 1, h: loaded?.image.height ?? 1 };
  const move = (by: { dx: number; dy: number }) => setCrop((c) => dragged(c, size, BOX, by));
  const at = placement(size, squareRegion(size, crop), BOX);
  return (
    <Dialog open={file !== null} onClose={onClose} title={t("picture.crop.title")}>
      <p class="mb-3 text-sm text-zinc-600">{t("picture.crop.hint")}</p>
      <div class="flex flex-col items-center gap-3">
        <div
          role="img"
          aria-label={t("picture.crop.preview")}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the arrow keys move the picture.
          tabIndex={0}
          class={`relative cursor-move touch-none overflow-hidden bg-zinc-100 select-none ${p.round ? "rounded-full" : "rounded-2xl"} ${FOCUS}`}
          style={{ width: `${BOX}px`, height: `${BOX}px` }}
          onPointerDown={(e) => {
            drag.current = { x: e.clientX, y: e.clientY };
            (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
          }}
          onPointerMove={(e) => {
            // Pressed (a button, a finger): dragging.
            if (e.buttons > 0) {
              const from = drag.current;
              move({ dx: e.clientX - from.x, dy: e.clientY - from.y });
              drag.current = { x: e.clientX, y: e.clientY };
            }
          }}
          onKeyDown={(e) => {
            const by = ARROWS[e.key];
            if (by) {
              e.preventDefault();
              move(by);
            }
          }}
        >
          {loaded ? (
            <img
              src={loaded.url}
              alt=""
              width={Math.round(at.width)}
              height={Math.round(at.height)}
              draggable={false}
              class="pointer-events-none absolute max-w-none"
              style={{ width: `${at.width}px`, height: `${at.height}px`, left: `${at.left}px`, top: `${at.top}px` }}
            />
          ) : null}
        </div>
        <label class="flex w-full max-w-60 items-center gap-3 text-sm text-zinc-700">
          {t("picture.zoom")}
          <input
            type="range"
            aria-label={t("picture.zoom")}
            min={minZoom(size)}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            value={crop.zoom}
            onInput={(e) => setCrop((c) => zoomed(c, size, Number((e.target as HTMLInputElement).value)))}
            class="flex-1 accent-blue-600"
          />
        </label>
      </div>
      <DialogActions>
        <Button answer="cancel" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" busy={saving} disabled={!loaded} onClick={save}>
          {t("picture.use")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Choosing an image file, then cropping it: a button (`children` its label) that opens the file picker, and the crop
 * dialog for what was picked. `onPicture` gets the square to save.
 */
function PictureUpload(p: {
  round: boolean;
  onPicture: (picture: Blob) => Promise<void> | void;
  children: ComponentChildren;
  disabled?: boolean;
  size?: "sm";
}) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const close = useRef(() => setFile(null)).current;
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        class="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          const el = e.currentTarget;
          setFile(el.files?.[0] ?? null);
          el.value = "";
        }}
      />
      <Button {...(p.size && { size: p.size })} disabled={p.disabled} onClick={() => input.current?.click()}>
        {p.children}
      </Button>
      <CropDialog file={file} round={p.round} onClose={close} onDone={p.onPicture} />
    </>
  );
}

/**
 * A picture setting (yours, an organization's logo): what it is now (`preview`), a button to upload one (cropped
 * first) and, when there's one, to remove it. Read-only without `onUpload`.
 */
export function PictureField(p: {
  label: string;
  hint: string;
  preview: ComponentChildren;
  round: boolean;
  has: boolean;
  onUpload?: (picture: Blob) => Promise<void>;
  onRemove?: () => Promise<void>;
}) {
  const [remove, removing] = useAction(async () => {
    await p.onRemove?.();
  });
  return (
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-zinc-800">{p.label}</span>
      <div class="flex flex-wrap items-center gap-3">
        {p.preview}
        {p.onUpload ? (
          <PictureUpload round={p.round} onPicture={p.onUpload}>
            {t(p.has ? "picture.change" : "picture.upload")}
          </PictureUpload>
        ) : null}
        {p.onUpload && p.has ? (
          <Button variant="ghost" busy={removing} onClick={remove}>
            {t("common.remove")}
          </Button>
        ) : null}
      </div>
      <p class="text-xs text-zinc-500">{p.hint}</p>
    </div>
  );
}

/** The key of a choice, to compare it with the options'. */
const keyOf = (c: AgentPicture): string => (c.kind === "preset" ? c.preset : c.kind);

/** One option of the chooser: a picture, as a radio. */
function Choice(p: { label: string; checked: boolean; onPick: () => void; children: ComponentChildren }) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a grid of pictures, each a radio.
    <button
      type="button"
      role="radio"
      aria-checked={p.checked}
      aria-label={p.label}
      title={p.label}
      onClick={p.onPick}
      class={`rounded-full p-0.5 ring-2 ${p.checked ? "ring-blue-600" : "ring-transparent hover:ring-zinc-300"} ${FOCUS}`}
    >
      {p.children}
    </button>
  );
}

const PICK = 36;

/**
 * Choosing an agent's picture: none (its kind's icon), a provider's logo, one of the robots, or an image of your own
 * (uploaded with the form, cropped first). `fallback` draws "none".
 */
export function AgentPictureChooser(p: {
  value: AgentPicture;
  onValue: (v: AgentPicture) => void;
  fallback: ComponentChildren;
}) {
  // The image of its own (the one it has, or one picked here), still offered after choosing another.
  const [own, setOwn] = useState(p.value.kind === "upload" || p.value.kind === "current" ? p.value : null);
  const current = keyOf(p.value);
  const label = useId();
  return (
    <div class="flex flex-col gap-1.5">
      <span id={label} class="text-sm font-medium text-zinc-800">
        {t("agents.picture")}
      </span>
      <div role="radiogroup" aria-labelledby={label} class="flex flex-wrap items-center gap-1.5">
        <Choice
          label={t("agents.picture.none")}
          checked={current === "none"}
          onPick={() => p.onValue({ kind: "none" })}
        >
          {p.fallback}
        </Choice>
        {[...ROBOT_PICTURES, ...PROVIDER_PICTURES].map((key) => (
          <Choice
            key={key}
            label={presetName(key)}
            checked={current === key}
            onPick={() => p.onValue({ kind: "preset", preset: key })}
          >
            <PresetPicture preset={key} size={PICK} />
          </Choice>
        ))}
        {own ? (
          <Choice label={t("agents.picture.own")} checked={current === own.kind} onPick={() => p.onValue(own)}>
            <Photo url={own.url} size={PICK} />
          </Choice>
        ) : null}
        <PictureUpload
          round={true}
          size="sm"
          onPicture={(blob) => {
            const picked: AgentPicture = { kind: "upload", blob, url: URL.createObjectURL(blob) };
            setOwn(picked);
            p.onValue(picked);
          }}
        >
          {t("agents.picture.upload")}
        </PictureUpload>
      </div>
    </div>
  );
}
